import React, { memo } from 'react'
import { Paper } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { Maybe } from 'types/graphqlTypes'
import { brandColors } from 'transfix-ui'
import { getActiveCommissionTier, getTierRangeLabel } from '../utils'

const useCommissionTiersTableStyles = makeStyles(theme => ({
  root: {
    padding: `${theme.spacing(2.5)} ${theme.spacing(2.5)} ${theme.spacing(1.5)}`,
    width: '350px',
  },
  tiers: {
    marginTop: theme.spacing(1),
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1),
    borderRadius: theme.spacing(0.5),
    '&.active': {
      backgroundColor: brandColors.coolGray1,
      fontWeight: theme.typography.fontWeightBold,
    },
  },
}))

export interface ICommissionTiersTableProps {
  rate?: Maybe<number>
  tier1Max?: number
  tier2Max?: number
  tier3Max?: number
  /**
   * @default false
   */
  loading?: boolean
}

const CommissionTiersTable = memo<ICommissionTiersTableProps>(
  ({ loading = false, rate, tier1Max, tier2Max, tier3Max }) => {
    const classes = useCommissionTiersTableStyles()

    const activeTier = getActiveCommissionTier({
      rate,
      tier1Max,
      tier2Max,
      tier3Max,
    })

    const tiers = [
      {
        tierNumber: 1,
        rangeLabel: getTierRangeLabel({ tierNumber: 1, tier1Max, tier2Max, tier3Max }),
        active: activeTier === 1,
      },
      {
        tierNumber: 2,
        rangeLabel: getTierRangeLabel({ tierNumber: 2, tier1Max, tier2Max, tier3Max }),
        active: activeTier === 2,
      },
      {
        tierNumber: 3,
        rangeLabel: getTierRangeLabel({ tierNumber: 3, tier1Max, tier2Max, tier3Max }),
        active: activeTier === 3,
      },
      {
        tierNumber: 4,
        rangeLabel: getTierRangeLabel({ tierNumber: 4, tier1Max, tier2Max, tier3Max }),
        active: activeTier === 4,
      },
    ]

    return (
      <Paper className={classes.root}>
        <Typography variant='subtitle1'>Commission Tiers</Typography>
        <div className={classes.tiers}>
          {tiers.map(({ active, rangeLabel, tierNumber }) => (
            <div
              key={tierNumber}
              className={`${classes.row} ${active ? 'active' : ''}`}
              data-test={`tier-${tierNumber}-row`}>
              <div>Tier {tierNumber}</div>
              <div>{loading ? <Skeleton variant='text' width={75} /> : rangeLabel}</div>
            </div>
          ))}
        </div>
      </Paper>
    )
  }
)

export default CommissionTiersTable
